<script setup lang="ts">
import { computed } from 'vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import DolphinMultiSelector from '@/components/atoms/DolphinMultiSelector.vue'
import LabeledInput from './LabeledInput.vue'
import {
  KollegeUsersFilterAntiSocialStatus,
  KollegeUsersFilterUserStatus,
} from '@/types/schema.generated'
import FlatButton from '@/components/atoms/FlatButton.vue'
import type { OkaneKollegeIndexPageFilterConditionsQueryFragment } from './OkaneKollegeIndexPageFilterConditions.generated'
import InstructorSelector from './InstructorSelector.vue'
import type { OkaneKollegeIndexPageFilterConditions } from '../schema'
import type { Option } from '@/types/app'

type UserStatusOption = {
  label: string
  value: KollegeUsersFilterUserStatus
}

const filterConditions = defineModel<OkaneKollegeIndexPageFilterConditions>(
  'filterConditions',
  {
    required: true,
  }
)

const selectedUserStatusOptions = computed({
  get: () =>
    userStatusOptions.filter(option =>
      filterConditions.value.userStatuses.includes(option.value)
    ),
  set: (value: UserStatusOption[]) => {
    filterConditions.value = {
      ...filterConditions.value,
      userStatuses: value.map(option => option.value),
    }
  },
})

const props = defineProps<{
  loading: boolean
  payload: OkaneKollegeIndexPageFilterConditionsQueryFragment
}>()

const emits = defineEmits<{
  search: []
  reset: []
}>()

const isAntiSocialOptions: Option<KollegeUsersFilterAntiSocialStatus | null>[] =
  [
    { text: '指定なし', value: null },
    { text: '未チェック', value: KollegeUsersFilterAntiSocialStatus.Unknown },
    {
      text: '反社ではない',
      value: KollegeUsersFilterAntiSocialStatus.NotAntiSocial,
    },
    { text: '反社', value: KollegeUsersFilterAntiSocialStatus.IsAntiSocial },
  ]
const userStatusOptions: UserStatusOption[] = [
  {
    label: '無料体験会未予約',
    value: KollegeUsersFilterUserStatus.TrialNotBooked,
  },
  {
    label: '無料体験会予約済み',
    value: KollegeUsersFilterUserStatus.TrialBooked,
  },
  {
    label: '未申込',
    value: KollegeUsersFilterUserStatus.TrialCompletedAndCourseNotApplied,
  },
  {
    label: '講師登録待ち',
    value: KollegeUsersFilterUserStatus.CourseApplied,
  },
  { label: '受講中', value: KollegeUsersFilterUserStatus.CourseInProgress },
  {
    label: '受講完了',
    value: KollegeUsersFilterUserStatus.CourseCompleted,
  },
  { label: '解約済み', value: KollegeUsersFilterUserStatus.CourseCancelled },
]
</script>

<template>
  <form :class="$style.conditions" @submit.prevent="emits('search')">
    <LabeledInput :class="$style.conditions__username">
      <template #title>ユーザー名</template>
      <DolphinTextInput
        type="text"
        placeholder="ユーザー名"
        v-model="filterConditions.userName"
        :class="$style.conditions__input"
      />
    </LabeledInput>
    <LabeledInput :class="$style.conditions__instructor">
      <template #title>講師</template>
      <InstructorSelector
        :payload="props.payload"
        v-model="filterConditions.instructorIds"
      />
    </LabeledInput>
    <LabeledInput :class="$style.conditions__antiSocial">
      <template #title>反社チェック</template>
      <DolphinSelector
        :options="isAntiSocialOptions"
        v-model="filterConditions.isAntiSocial"
        placeholder="指定なし"
      />
    </LabeledInput>
    <LabeledInput :class="$style.conditions__status">
      <template #title>ユーザーステータス</template>
      <DolphinMultiSelector
        :options="userStatusOptions"
        v-model="selectedUserStatusOptions"
        track-by="value"
        label="label"
        allow-empty
        :searchable="false"
      />
    </LabeledInput>
    <FlatButton
      type="button"
      :class="$style.conditions__reset"
      variant="secondary"
      size="large"
      @click="emits('reset')"
      >リセット</FlatButton
    >
    <FlatButton
      type="submit"
      :class="$style.conditions__submit"
      variant="primary"
      size="large"
      :disabled="loading"
      >検索</FlatButton
    >
  </form>
</template>

<style lang="scss" module>
.conditions {
  display: grid;
  grid-template:
    'username instructor antiSocial .....  ......' auto
    'status   status     status     reset  submit' auto
    / auto 1fr auto auto;
  gap: 8px;
  padding: 16px;
  background-color: $color-surface-secondary;
  border-radius: 12px;

  &__username {
    grid-area: username;
  }

  &__instructor {
    grid-area: instructor;
  }

  &__antiSocial {
    grid-area: antiSocial;
  }

  &__status {
    grid-area: status;
  }

  &__reset {
    grid-area: reset;
    margin-top: 26px;
  }

  &__submit {
    grid-area: submit;
    margin-top: 26px;
  }
}
</style>
